import {supabase} from '@/shared/api/supabase.ts';
import {userRoleOptions} from '@/shared/types/common.ts';
import {Employee, EMPLOYEES_TABLE, employeeSchema} from '@/shared/types/employee.ts';

/**
 * Get all active employees with employee or owner roles.
 * Used by RegisterServiceSheet to populate employee selection.
 */
export const getActiveEmployees = async (): Promise<Employee[]> => {
    const {data, error} = await supabase
        .from(EMPLOYEES_TABLE.TABLE)
        .select('*')
        .eq(EMPLOYEES_TABLE.ACTIVE, true)
        .in(EMPLOYEES_TABLE.ROLE, [userRoleOptions.enum.employee, userRoleOptions.enum.owner])
        .order(EMPLOYEES_TABLE.NAME);

    if (error) {
        console.error('Error fetching employees:', error);
        throw new Error('Failed to retrieve employees');
    }

    // Validate with Zod
    const parseResult = employeeSchema.array().safeParse(data);
    if (!parseResult.success) {
        console.error('Employee data validation failed:', parseResult.error);
        return [];
    }

    return parseResult.data;
};

/**
 * Get an active employee by email.
 *
 * @param email - The email to look up
 * @throws Error if employee not found or validation fails
 */
export const getActiveEmployeeByEmail = async (email: string): Promise<Employee> => {
    const {data, error} = await supabase
        .from(EMPLOYEES_TABLE.TABLE)
        .select('*')
        .eq(EMPLOYEES_TABLE.EMAIL, email.toLowerCase().trim())
        .eq(EMPLOYEES_TABLE.ACTIVE, true)
        .maybeSingle();

    if (error) {
        console.error('Error fetching employee:', error);
        throw new Error('Failed to retrieve employee');
    }

    if (!data) {
        throw new Error('Employee not found');
    }

    // Validate with Zod
    const parseResult = employeeSchema.safeParse(data);
    if (!parseResult.success) {
        console.error('Employee data validation failed:', parseResult.error);
        throw new Error('Employee data validation failed');
    }

    return parseResult.data;
};
